import { useTranslation } from "react-i18next";
import { Link } from "wouter";
import { Mail } from "lucide-react";
import { trackCTA, trackConversion } from "@/lib/gtag";

export function Footer() {
  const { t } = useTranslation();

  return (
    <footer className="bg-black py-12 border-t border-white/10">
      <div className="container mx-auto px-4">
        <div className="grid md:grid-cols-4 gap-8 mb-12">
          {/* Brand column */}
          <div>
            <div className="flex items-center gap-2 mb-4">
              <img src="/traverba.svg" alt="Traverba" className="w-6 h-6" />
              <span className="text-lg font-bold tracking-tight italic" style={{ fontFamily: "Arial, sans-serif" }}>TRAVERBA</span>
            </div>
            <p className="text-muted-foreground max-w-sm mb-4">
              {t('footer.tagline')}
            </p>
            <a href="mailto:support@traverba.com" className="flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors" onClick={() => { trackCTA("email_click", "Footer Email"); trackConversion("LPSrCKS9uo4cEOiQqtAD"); }}>
              <Mail className="w-4 h-4" />
              <span>support@traverba.com</span>
            </a>

            {/* Community / social */}
            <div className="mt-6">
              <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">{t('footer.community')}</h3>
              <div className="flex items-center gap-3">
                <a
                  href="https://discord.gg/DKg3f4eSEk"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={t('footer.joinDiscord')}
                  title={t('footer.joinDiscord')}
                  onClick={() => trackCTA("discord_click", "Footer Discord")}
                  className="flex items-center gap-2 bg-white/5 hover:bg-[#5865F2] border border-white/10 rounded-xl px-4 py-2.5 text-sm font-medium transition-colors group"
                >
                  <svg className="w-5 h-5 text-muted-foreground group-hover:text-white transition-colors" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                    <path d="M20.317 4.369a19.79 19.79 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.249a18.27 18.27 0 0 0-5.487 0 12.6 12.6 0 0 0-.617-1.25.077.077 0 0 0-.079-.036A19.74 19.74 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.2 14.2 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.1 13.1 0 0 1-1.872-.892.077.077 0 0 1-.008-.128c.126-.094.252-.192.372-.291a.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.009c.12.099.246.198.373.292a.077.077 0 0 1-.006.127 12.3 12.3 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.84 19.84 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.028ZM8.02 15.331c-1.182 0-2.157-1.086-2.157-2.419 0-1.333.956-2.418 2.157-2.418 1.21 0 2.176 1.095 2.157 2.418 0 1.333-.956 2.419-2.157 2.419Zm7.975 0c-1.183 0-2.157-1.086-2.157-2.419 0-1.333.955-2.418 2.157-2.418 1.21 0 2.176 1.095 2.157 2.418 0 1.333-.946 2.419-2.157 2.419Z"/>
                  </svg>
                  <span>{t('footer.joinDiscord')}</span>
                </a>
              </div>
            </div>
          </div>

          {/* Product column */}
          <div>
            <h3 className="font-bold mb-4 text-white">{t('footer.product')}</h3>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li><Link href="/features" className="hover:text-primary transition-colors">{t('nav.features')}</Link></li>
              <li><Link href="/pricing" className="hover:text-primary transition-colors">{t('nav.pricing')}</Link></li>
              <li><Link href="/download" className="hover:text-primary transition-colors">{t('nav.download')}</Link></li>
              <li><Link href="/gemini-api-key" className="hover:text-primary transition-colors">{t('footer.geminiApiKey')}</Link></li>
              <li><Link href="/groq-api-key" className="hover:text-primary transition-colors">{t('footer.groqApiKey')}</Link></li>
              <li><Link href="/byo-llm-setup" className="hover:text-primary transition-colors">{t('footer.byoLlm')}</Link></li>
            </ul>
          </div>

          {/* Company column */}
          <div>
            <h3 className="font-bold mb-4 text-white">{t('footer.company')}</h3>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li><Link href="/about" className="hover:text-primary transition-colors">{t('nav.about')}</Link></li>
              <li><Link href="/blog" className="hover:text-primary transition-colors">{t('nav.blog')}</Link></li>
              <li><Link href="/contact" className="hover:text-primary transition-colors">{t('footer.contactUs')}</Link></li>
            </ul>
          </div>

          {/* Download column */}
          <div>
            <h3 className="font-bold mb-4 text-white">{t('footer.download')}</h3>
            <div className="flex flex-col gap-3">
              <a href="https://apps.apple.com/app/id6773778315" target="_blank" rel="noopener noreferrer" onClick={() => trackCTA("app_store_click", "Footer iOS")} className="flex items-center gap-3 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl px-4 py-2.5 transition-colors">
                <svg className="w-7 h-7 text-foreground" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                </svg>
                <div>
                  <div className="text-[10px] text-muted-foreground leading-tight">Download on the</div>
                  <div className="text-sm font-semibold leading-tight">App Store</div>
                </div>
              </a>
              <a href="https://play.google.com/store/apps/details?id=com.coflows.traverba" target="_blank" rel="noopener noreferrer" onClick={() => trackCTA("play_store_click", "Footer Android")} className="flex items-center gap-3 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl px-4 py-2.5 transition-colors">
                <svg className="w-7 h-7 text-foreground" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M22.018 13.298l-3.919 2.218-3.515-3.493 3.543-3.521 3.891 2.202a1.49 1.49 0 0 1 0 2.594zM1.337.924a1.486 1.486 0 0 0-.112.568v21.017c0 .217.045.419.124.6l11.155-11.087L1.337.924zm12.207 10.065l3.258-3.238L3.45.195a1.466 1.466 0 0 0-.946-.179l11.04 10.973zm0 2.067l-11 10.933c.298.036.612-.016.906-.183l13.324-7.54-3.23-3.21z"/>
                </svg>
                <div>
                  <div className="text-[10px] text-muted-foreground leading-tight">Get it on</div>
                  <div className="text-sm font-semibold leading-tight">Google Play</div>
                </div>
              </a>
            </div>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="pt-8 border-t border-white/10 flex flex-col md:flex-row justify-between items-center gap-4 text-sm text-muted-foreground">
          <p>{t('footer.copyright')}</p>
          <div className="flex gap-6">
            <Link href="/privacy-policy" className="hover:text-white transition-colors">{t('footer.privacyPolicy')}</Link>
            <Link href="/terms-of-service" className="hover:text-white transition-colors">{t('footer.termsOfService')}</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
